import Alert from "@mui/material/Alert";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { FileMetadataAndTopics } from "@/shared/components/FileMetadataAndTopics";
import { useAuditIdentityOptions } from "@/shared/components/principal";
import {
  QuickSearch,
  QuickSearchButton,
  ResourceView,
  activeFiltersToQuerySubmission,
  renderPrincipal,
  toOptionsResolver,
  useQuickSearchHotkey,
  useResourceView,
  type FilterDefinition,
  type QuickSearchProperty,
  type RoboqlMenuConfig,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { AssociationType } from "@/shared/domain/association";
import { FileRecord } from "@/shared/domain/files";
import {
  filesScopeToCondition,
  type FilesQueryScope,
} from "@/shared/domain/files/FilesQueryScope";
import {
  fileQueryKeys,
  useFileMetadataKeys,
  useOrgFileTagSearch,
} from "@/shared/domain/files/hooks";
import {
  composeQueryBody,
  formatMetadataValue,
  QueryContentMode,
  QueryTarget,
  type EntityIdGetter,
} from "@/shared/domain/query";
import {
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { useFileTablePreferences } from "@/shared/domain/users/hooks";
import {
  ColumnVisibility,
  DataTable,
  FLEX_FILL_COL_DEF,
  EmptyCell,
  buildColumnsForPreferences,
  cellStyles,
  colId,
  createColumnHelper,
  renderDashOr,
  renderDashOrValue,
  type ColDef,
  type ColumnPickerOption,
  type DataTableLoadingState,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { TagGroup } from "@/shared/foundation/primitives/Tag";
import { Goto } from "@/shared/navigation/Goto";
import { formatByteSize } from "@/shared/text";

import styles from "./FilesList.module.css";

const getFileId: EntityIdGetter<FileRecord> = (f) => f.file_id;

const ROBOQL_CONFIG: RoboqlMenuConfig = { target: QueryTarget.Files };

// Quick find routes a typed value to one field: an `fl_`-shaped value to the
// id, everything else to the name. Categorical filters (tags) live in the
// Filter menu, not here.
const QUICK_SEARCH_PROPERTIES: QuickSearchProperty[] = [
  {
    field: "file_id",
    name: "ID",
    // A file id is `fl_` plus non-space characters — anchored so a phrase
    // merely starting that way falls through to the name search.
    pattern: /^fl_\S+$/,
  },
  { field: "name", name: "Name", fallback: true },
];

const columnHelper = createColumnHelper<FileRecord>();

const STATIC_FILE_COLUMNS: ColDef<FileRecord>[] = [
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 3,
    minWidth: 200,
    sortable: false,
    cellRenderer: (p: ICellRendererParams<FileRecord, string>) => {
      const fileId = p.data?.file_id;
      const name = p.value ?? "";
      if (!name) {
        return <span className={cellStyles.placeholder}>Untitled</span>;
      }
      if (!fileId) {
        return <span className={cellStyles.primaryContent}>{name}</span>;
      }
      return (
        <RobotoLinkHoverUnderline
          to={`${Goto.route.Files}/${fileId}`}
          className={cellStyles.primaryContent}
        >
          {name}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.timestamp("created", {
    headerName: "Created",
    minWidth: 160,
    sortable: true,
    variant: "relative",
  }),
  {
    colId: "source",
    field: "origination",
    headerName: "Source",
    minWidth: 200,
    sortable: false,
    cellClass: cellStyles.monoCell,
    cellRenderer: (p: ICellRendererParams<FileRecord, string>) => {
      const value = p.value ?? "";
      if (!value) {
        return <EmptyCell />;
      }
      if (value.startsWith("iv_")) {
        return (
          <RobotoLinkHoverUnderline
            to={`${Goto.route.Invocations}/${value}`}
            className={cellStyles.idCell}
          >
            {value}
          </RobotoLinkHoverUnderline>
        );
      }
      return value;
    },
  },
  columnHelper.accessor("association_id", {
    headerName: "Association ID",
    minWidth: 200,
    sortable: false,
    cellClass: cellStyles.monoCell,
    cellRenderer: (p: ICellRendererParams<FileRecord, string>) => {
      const value = p.value ?? "";
      if (!value) {
        return <EmptyCell />;
      }
      // Link to the dataset only for a dataset association (or an unspecified
      // type — historically the association was always a dataset). This needs
      // backfill once we introduce new association types.
      // Other association types render as plain text for now.
      const associationType = p.data?.association_type;
      if (
        associationType !== AssociationType.Dataset &&
        associationType !== undefined
      ) {
        return <span className={cellStyles.idCell}>{value}</span>;
      }
      return (
        <RobotoLinkHoverUnderline
          to={`${Goto.route.Datasets}/${value}`}
          className={cellStyles.idCell}
        >
          {value}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 3,
    minWidth: 180,
    cellClass: cellStyles.tagsCell,
    valueFormatter: (p) => (p.value ?? []).join(", "),
    cellRenderer: renderDashOr(
      (p: ICellRendererParams<FileRecord, string[]>) => (
        <TagGroup tags={p.value ?? []} maxDisplayCount={3} />
      ),
    ),
  }),
  columnHelper.accessor("file_id", {
    headerName: "File ID",
    minWidth: 200,
    sortable: false,
    cellRenderer: (p: ICellRendererParams<FileRecord, string>) => {
      const id = p.value;
      if (!id) {
        return null;
      }
      return (
        <RobotoLinkHoverUnderline
          to={`${Goto.route.Files}/${id}`}
          className={cellStyles.idCell}
        >
          {id}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.accessor("created_by", {
    headerName: "Created by",
    minWidth: 150,
    sortable: false,
    cellRenderer: renderPrincipal,
  }),
  columnHelper.timestamp("modified", {
    headerName: "Modified",
    minWidth: 160,
    sortable: true,
    variant: "relative",
  }),
  columnHelper.accessor("modified_by", {
    headerName: "Modified by",
    minWidth: 150,
    sortable: false,
    cellRenderer: renderPrincipal,
  }),
  columnHelper.accessor("relative_path", {
    headerName: "Path",
    minWidth: 150,
    sortable: false,
    cellClass: "truncate",
  }),
  columnHelper.accessor("size", {
    headerName: "Size",
    minWidth: 100,
    sortable: false,
    valueFormatter: (p) =>
      p.value === null || p.value === undefined ? "" : formatByteSize(p.value),
  }),
  columnHelper.accessor("org_id", {
    headerName: "Org ID",
    minWidth: 150,
    sortable: false,
    cellClass: cellStyles.idCell,
  }),
  columnHelper.accessor("uri", {
    headerName: "URI",
    minWidth: 150,
    sortable: false,
    cellClass: "truncate",
  }),
  columnHelper.accessor("status", {
    headerName: "Status",
    minWidth: 110,
    sortable: false,
  }),
  columnHelper.accessor("upload_id", {
    headerName: "Upload ID",
    minWidth: 180,
    sortable: false,
    cellClass: cellStyles.idCell,
  }),
  columnHelper.accessor("device_id", {
    headerName: "Device ID",
    minWidth: 150,
    sortable: false,
    cellClass: cellStyles.idCell,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor("description", {
    headerName: "Description",
    minWidth: 150,
    sortable: false,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor("ingestion_status", {
    headerName: "Ingestion Status",
    minWidth: 140,
    sortable: false,
  }),
  columnHelper.accessor("version", {
    headerName: "Version",
    minWidth: 90,
    sortable: false,
  }),
  columnHelper.accessor("fs_type", {
    headerName: "FS Type",
    minWidth: 100,
    sortable: false,
  }),
  columnHelper.accessor("parent_id", {
    headerName: "Parent ID",
    minWidth: 180,
    sortable: false,
    cellClass: cellStyles.idCell,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor("storage_type", {
    headerName: "Storage Type",
    minWidth: 130,
    sortable: false,
  }),
];

const DEFAULT_VISIBLE_IDS = [
  "name",
  "created",
  "source",
  "association_id",
  "tags",
];

const STATIC_PICKER_OPTIONS: ColumnPickerOption[] = STATIC_FILE_COLUMNS.map(
  (c) => ({
    id: colId(c),
    label: c.headerName ?? colId(c),
  }),
);

function buildMetadataColumn(metadataKey: string): ColDef<FileRecord> {
  return {
    colId: `metadata.${metadataKey}`,
    headerName: metadataKey,
    // Floor so a flex metadata column doesn't collapse when the grid overflows.
    minWidth: 120,
    valueGetter: (p) => p.data?.metadata?.[metadataKey],
    valueFormatter: (p) => formatMetadataValue(p.value),
    cellRenderer: renderDashOrValue,
    sortable: false,
  };
}

export interface FilesListProps {
  scope: FilesQueryScope;
  /** Bulk-action options surfaced in the DataTable.Selection menu. */
  selectionOptions?: SelectionOption<FileRecord>[];
}

export function FilesList({ scope, selectionOptions = [] }: FilesListProps) {
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const quickSearchState = useQuickSearchHotkey();
  const { preferences, savePreferences } = useFileTablePreferences();

  const metadataKeysQuery = useFileMetadataKeys(resolvedOrgId);

  // Directories backing the `created_by` / `modified_by` identity filters.
  const identityOptions = useAuditIdentityOptions(resolvedOrgId);

  const [initialColumnIds] = React.useState(() => preferences.columns);

  const columns = React.useMemo<ColDef<FileRecord>[]>(
    () =>
      buildColumnsForPreferences<FileRecord>({
        staticColumns: STATIC_FILE_COLUMNS,
        dynamicColumns: (metadataKeysQuery.data ?? []).map(buildMetadataColumn),
        preferenceOrder: initialColumnIds,
      }),
    [initialColumnIds, metadataKeysQuery.data],
  );

  const availableColumns = React.useMemo<ColumnPickerOption[]>(
    () => [
      ...STATIC_PICKER_OPTIONS,
      ...(metadataKeysQuery.data ?? []).map<ColumnPickerOption>((key) => ({
        id: `metadata.${key}`,
        label: key,
      })),
    ],
    [metadataKeysQuery.data],
  );

  const view = useResourceView<FileRecord>({
    getRowId: getFileId,
    initialSort: { columnId: "created", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_IDS,
  });

  // Filter definitions are restricted to fields the backend exposes via
  // `FILE_FIELDS` in `roboql/model/core.py`. Excluded:
  //   - parent_id, version, association_type, ingestion_status, fs_type,
  //     storage_type: not in FILE_FIELDS (backend rejects the condition).
  //   - status: we only surface "available" files, not "reserved" or "deleted",
  //     so this filter would buy us nothing.
  //   - org_id: always implicitly scoped to the current org by the
  //     structured-query backend.
  //   - metadata: dynamic, handled by Milestone 4.
  const searchFileTags = useOrgFileTagSearch(resolvedOrgId);

  const filterDefinitions: FilterDefinition[] = React.useMemo(
    () => [
      { type: "string", field: "file_id", name: "File Id" },
      { type: "string", field: "name", name: "Name" },
      { type: "string", field: "relative_path", name: "Relative Path" },
      { type: "string", field: "description", name: "Description" },
      { type: "string", field: "uri", name: "URI" },
      { type: "string", field: "device_id", name: "Device Id" },
      { type: "string", field: "origination", name: "Source" },
      { type: "string", field: "upload_id", name: "Upload Id" },
      { type: "string", field: "association_id", name: "Association Id" },
      { type: "date", field: "created", name: "Created" },
      {
        type: "identity",
        field: "created_by",
        name: "Created By",
        options: identityOptions,
      },
      { type: "date", field: "modified", name: "Modified" },
      {
        type: "identity",
        field: "modified_by",
        name: "Modified By",
        options: identityOptions,
      },
      { type: "numeric", field: "size", name: "Size" },
      {
        type: "set",
        field: "tags",
        name: "Tags",
        options: toOptionsResolver(searchFileTags),
      },
    ],
    [identityOptions, searchFileTags],
  );

  const submission = activeFiltersToQuerySubmission({
    filters: view.filters,
    sort: view.table.sort,
  });

  const query = composeQueryBody({
    scopeCondition: filesScopeToCondition(scope),
    filterCondition: submission.filterCondition,
    sort: submission.sort,
    maxResults: submission.maxResults,
  });

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Files,
    contentMode: QueryContentMode.RecordWithMeta,
    queryKey: fileQueryKeys.list(resolvedOrgId, scope, query),
  });

  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<FileRecord>(
    queryId,
    view.table.currentPage,
    view.table.pageSize,
  );

  const countQuery = useQueryCount(queryId);

  const rows: FileRecord[] = React.useMemo(
    () => pageQuery.data ?? [],
    [pageQuery.data],
  );

  const renderExpandedRow = React.useCallback((file: FileRecord) => {
    return (
      <FileMetadataAndTopics
        fileId={file.file_id}
        fileVersion={file.version}
        resourceOwnerId={file.org_id}
        readonly
      />
    );
  }, []);

  let loading: DataTableLoadingState = "idle";
  if (submitQuery.isLoading || pageQuery.isLoading) {
    loading = "foreground";
  } else if (submitQuery.isFetching || pageQuery.isFetching) {
    loading = "background";
  }
  const fetchError = submitQuery.error ?? pageQuery.error;

  const hasSelectionOptions = selectionOptions.length > 0;

  return (
    <>
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {fetchError.message}
        </Alert>
      )}
      <QuickSearch
        open={quickSearchState.isOpen}
        onClose={quickSearchState.close}
        properties={QUICK_SEARCH_PROPERTIES}
        placeholder={"Search by ID or name…"}
        onSubmit={(filter) => {
          view.clearFilters();
          view.commit({ type: "structured", filter });
        }}
      />
      <ResourceView.Root>
        <ResourceView.Actions>
          <QuickSearchButton
            onClick={quickSearchState.open}
            label="Find files"
          />
          <ResourceView.Filters
            filterDefinitions={filterDefinitions}
            filters={view.filters}
            onFilterSelected={view.commit}
            roboql={ROBOQL_CONFIG}
          />
          <ColumnVisibility
            options={availableColumns}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
          {hasSelectionOptions && (
            <ResourceView.Selection<FileRecord>
              selection={view.table.selection}
              clearSelection={view.table.clearSelection}
              options={selectionOptions}
            />
          )}
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          filters={view.filters}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onUpdate={view.updateFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
          roboql={ROBOQL_CONFIG}
        />
        <DataTable<FileRecord>
          defaultColDef={FLEX_FILL_COL_DEF}
          table={view.table}
          hasNextPage={pageQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent="No files"
          totalRowCount={countQuery.data}
          selectionEnabled={hasSelectionOptions}
          expansion={{ renderRow: renderExpandedRow }}
        />
      </ResourceView.Root>
    </>
  );
}
